import type { ModelMessage } from 'ai';

/**
 * Convention for letting a tool hand the model an image it can actually *see*
 * (a vision block), rather than a JSON blob of base64 text.
 *
 * A tool returns a {@link ToolImageEnvelope} (built with {@link toolImage}). The
 * envelope survives storage/rehydration like any other tool output. When the
 * conversation is rendered for the LLM, {@link applyToolImageOutputs} rewrites
 * the tool-result output into the AI SDK `content` form, which the provider
 * (e.g. Anthropic) turns into a real image content block inside `tool_result`.
 *
 * Mirrors the sigil-envelope pattern used by `incomplete()` — kept in the SDK
 * core so render code (which cannot import from the server) and server-side
 * tools can share one contract.
 */
export const TOOL_IMAGE_SIGIL = '@@cotera-tool-image' as const;

/**
 * Image media types that the vision-capable models accept. Kept in sync with
 * `SUPPORTED_FILE_MEDIA_TYPES` in message.ts (minus PDF, which is a document).
 */
export const SUPPORTED_TOOL_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export type ToolImageMimeType =
  (typeof SUPPORTED_TOOL_IMAGE_MIME_TYPES)[number];

export type ToolImageEnvelope = {
  [TOOL_IMAGE_SIGIL]: true;
  /** Base-64 encoded image data (no `data:` URI prefix). */
  data: string;
  /** IANA media type, e.g. `image/png`. */
  mimeType: string;
  /**
   * Optional short caption shown to the model alongside the image (filename,
   * dimensions, source, etc). Keep this small — the base64 data carries the
   * image; this is just context.
   */
  text: string | null;
};

/** Wrap image bytes so the model receives them as a viewable image. */
export function toolImage(params: {
  data: string;
  mimeType: string;
  text?: string | null;
}): ToolImageEnvelope {
  return {
    [TOOL_IMAGE_SIGIL]: true,
    data: params.data,
    mimeType: params.mimeType,
    text: params.text ?? null,
  };
}

/** True when a tool result carries the {@link toolImage} envelope. */
export function isToolImageEnvelope(
  value: unknown
): value is ToolImageEnvelope {
  if (
    value === null ||
    value === undefined ||
    typeof value !== 'object' ||
    Array.isArray(value)
  ) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    candidate[TOOL_IMAGE_SIGIL] === true &&
    typeof candidate['data'] === 'string' &&
    typeof candidate['mimeType'] === 'string'
  );
}

/**
 * Extract a tool-image envelope from a tool result value, tolerating the
 * `{ type: 'success', data: <output> }` success wrapper that some execution
 * paths put around tool output (e.g. the playground toolkit) before it reaches
 * the model. The ATC runtime stores the raw envelope, so both forms are handled.
 * Returns null when the value carries no envelope.
 */
export function extractToolImageEnvelope(
  value: unknown
): ToolImageEnvelope | null {
  if (isToolImageEnvelope(value)) {
    return value;
  }
  if (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { type?: unknown }).type === 'success'
  ) {
    const data = (value as { data?: unknown }).data;
    if (isToolImageEnvelope(data)) {
      return data;
    }
  }
  return null;
}

/** Short text left in the tool result, pointing at the injected image. */
function toolResultCaption(envelope: ToolImageEnvelope): string {
  return envelope.text !== null && envelope.text.trim() !== ''
    ? `${envelope.text} (shown in the following message)`
    : 'Image returned; shown in the following message.';
}

/**
 * Deliver images that a tool returned to the model.
 *
 * We can't put an image inside a `tool`-role message on every provider — the
 * OpenAI Chat Completions API (which Azure Foundry models like Coco's default
 * GPT-5.5 use) only allows text there and would `JSON.stringify` an image block
 * into base64 text the model can't see. Images *are* allowed in `user` messages
 * on every provider (that's why user-uploaded images already work), so for each
 * image envelope we:
 *   1. replace the tool result with a short text caption (so the tool call is
 *      still answered), and
 *   2. append a `user` message carrying the image as a `file` part right after
 *      the tool message.
 *
 * `convertToModelMessages` (called with no `tools` map) wraps every tool output
 * as `{ type: 'json' }`, so the envelope arrives inside `output.value`.
 *
 * No-op for messages without image envelopes.
 */
export function applyToolImageOutputs(
  messages: ModelMessage[]
): ModelMessage[] {
  const result: ModelMessage[] = [];
  for (const message of messages) {
    if (message.role !== 'tool' || !Array.isArray(message.content)) {
      result.push(message);
      continue;
    }
    const images: ToolImageEnvelope[] = [];
    const content = message.content.map((part) => {
      if (part.type === 'tool-result' && part.output.type === 'json') {
        const envelope = extractToolImageEnvelope(part.output.value);
        if (envelope !== null) {
          images.push(envelope);
          return {
            ...part,
            output: {
              type: 'text' as const,
              value: toolResultCaption(envelope),
            },
          };
        }
      }
      return part;
    });
    // Preserve object identity when nothing was rewritten.
    result.push(images.length > 0 ? { ...message, content } : message);
    for (const envelope of images) {
      result.push({
        role: 'user',
        content: [
          {
            type: 'file',
            data: envelope.data,
            mediaType: envelope.mimeType,
          },
        ],
      });
    }
  }
  return result;
}
